"use client";

import { memo, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/src/lib/utils";
import { useUserRole } from "@/hooks/useUserRole";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  useGetReviewerReportsQuery,
  useSubmitReviewerReportMutation,
} from "@/api/reviewer-reports/reviewerReportsApi";
import VerdictToggle from "./VerdictToggle";
import AddCommentModal from "./AddCommentModal";
import FullHistoryModal from "./FullHistoryModal";
import VerdictModal from "./VerdictModal";
import { CompactHistoryRow } from "./HistoryItems";
import { STATUS_COLOR, StatusIcon, VERDICT_GRADIENT, useVerdictLabels } from "./meta";
import { statusToCode, toHistoryEntries, type VerdictStatus } from "./types";

interface VerdictSectionProps {
  attemptId: number;
}

/** Сколько последних записей показывать в компактной карточке */
const COMPACT_LIMIT = 3;

/** Достаёт человекочитаемую ошибку из envelope privateBaseQuery */
function parseSubmitError(e: unknown): string {
  const data = (e as { data?: unknown })?.data as
    | { errors?: { comment?: string[]; exception?: string } }
    | undefined;
  if (data?.errors?.comment?.[0]) return data.errors.comment[0];
  if (data?.errors?.exception) return data.errors.exception;
  return "Error";
}

/**
 * Блок «Оценка отчёта ИИ» + «История заключений» (per attempt).
 * Данные — с бэка: GET/POST /api/assignment-reviewer-reports (см. reviewerReportsApi).
 */
const VerdictSection = memo(function VerdictSection({ attemptId }: VerdictSectionProps) {
  const labels = useVerdictLabels();
  const { isStudent } = useUserRole();
  const isMobile = useIsMobile();

  const { data, isLoading } = useGetReviewerReportsQuery(attemptId, { skip: !attemptId });
  const [submit, { isLoading: isSubmitting }] = useSubmitReviewerReportMutation();

  // Бэк отдаёт историю новыми сверху; маппим в view-модель + флаг «смена статуса/коммент».
  const history = useMemo(() => toHistoryEntries(data?.entities ?? []), [data]);
  const counters = data?.counters;

  // Общий вердикт = самая свежая запись в журнале (кто последний поставил — то и видят все).
  // НЕ зависит от того, кто сейчас смотрит: админ и проктор видят одинаковое значение.
  const sharedStatus: VerdictStatus | null = useMemo(
    () => history[0]?.status ?? null,
    [history],
  );

  // Десктоп: какой статус собираемся проставить/подтвердить (открывает модалку коммента)
  const [pendingStatus, setPendingStatus] = useState<VerdictStatus | null>(null);
  const [isFullHistoryOpen, setFullHistoryOpen] = useState(false);

  // Мобилка: единая модалка (просмотр/оставить).
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState<"view" | "compose">("view");
  const [mobileStatus, setMobileStatus] = useState<VerdictStatus | null>(null);

  const compactHistory = history.slice(0, COMPACT_LIMIT);
  const recentCommented = history.filter((e) => e.comment).slice(0, 2);

  // Тогл «Честно/Не честно»: мобилка → единая модалка (режим «Оставить»),
  // десктоп → модалка комментария.
  const handleSelect = (next: VerdictStatus) => {
    if (isMobile) {
      setMobileStatus(next);
      setMobileTab("compose");
      setMobileOpen(true);
    } else {
      setPendingStatus(next);
    }
  };

  // Открыть журнал: мобилка → единая модалка (режим «Комментарии»),
  // десктоп → модалка полной истории.
  const handleOpenHistory = () => {
    if (isMobile) {
      setMobileStatus(sharedStatus);
      setMobileTab("view");
      setMobileOpen(true);
    } else {
      setFullHistoryOpen(true);
    }
  };

  /** Отправка заключения → POST. Возвращает true при успехе. */
  const submitVerdict = async (status: VerdictStatus, comment: string): Promise<boolean> => {
    try {
      await submit({ attemptId, status: statusToCode(status), comment }).unwrap();
      return true; // список инвалидируется тегом
    } catch (e) {
      toast.error(parseSubmitError(e));
      return false;
    }
  };

  /** Сохранение из десктоп-модалки коммента. */
  const handleSaveComment = async (comment: string) => {
    if (!pendingStatus) return;
    const ok = await submitVerdict(pendingStatus, comment);
    if (ok) setPendingStatus(null);
  };

  if (isLoading) {
    return <Skeleton className="h-24 w-full rounded-2xl" />;
  }

  const canVote = !isStudent; // тоггл скрыт для студента (POST вернёт 403)

  // Студент без записей / нет данных — показывать нечего.
  if (!canVote && history.length === 0) return null;

  return (
    <>
      {canVote && history.length === 0 ? (
        <div
          className={cn(
            VERDICT_GRADIENT,
            "flex flex-col items-start gap-4 rounded-2xl px-6 py-5 md:flex-row md:items-center md:justify-between",
          )}
        >
          <p className="text-white text-lg font-semibold">{labels.bannerQuestion}</p>
          <VerdictToggle value={null} onSelect={handleSelect} disabled={isSubmitting} />
        </div>
      ) : (
        <Card className="p-4 gap-3">
          <div className="flex flex-col items-stretch gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-semibold">{labels.historyTitle}</h3>
              {/* {counters && counters.total > 0 && (
                <div className="flex items-center gap-2">
                  <CounterBadge status="honest" count={counters.true} />
                  <CounterBadge status="dishonest" count={counters.false} />
                </div>
              )} */}
            </div>
            {canVote && (
              <VerdictToggle
                value={sharedStatus}
                onSelect={handleSelect}
                disabled={isSubmitting}
              />
            )}
          </div>

          {compactHistory.length > 0 && (
            <button
              type="button"
              onClick={handleOpenHistory}
              className="text-left transition-colors self-stretch"
              aria-label={labels.fullHistoryTitle}
            >
              <div className="flex flex-col items-start self-stretch rounded-[6px] border border-[#E5E5E5] bg-white/50 hover:bg-white/70 transition-colors">
                {compactHistory.map((entry, i) => (
                  <CompactHistoryRow
                    key={entry.id}
                    entry={entry}
                    isLast={i === compactHistory.length - 1}
                  />
                ))}
              </div>
            </button>
          )}
        </Card>
      )}

      <AddCommentModal
        open={pendingStatus !== null}
        onOpenChange={(open) => !open && !isSubmitting && setPendingStatus(null)}
        status={pendingStatus}
        recentEntries={recentCommented}
        onSave={handleSaveComment}
        saving={isSubmitting}
      />

      {/* Десктоп: отдельная модалка полной истории. */}
      <FullHistoryModal
        open={isFullHistoryOpen}
        onOpenChange={setFullHistoryOpen}
        entries={history}
      />

      {/* Мобилка: единая модалка (просмотр/оставить). */}
      <VerdictModal
        open={mobileOpen}
        onOpenChange={(open) => !open && !isSubmitting && setMobileOpen(false)}
        entries={history}
        canVote={canVote}
        initialTab={mobileTab}
        initialStatus={mobileStatus}
        submitting={isSubmitting}
        onSubmit={submitVerdict}
      />
    </>
  );
});

// /** Бейдж счётчика текущих голосов (👍 N / 👎 N) */
// function CounterBadge({ status, count }: { status: VerdictStatus; count: number }) {
//   return (
//     <span
//       className="inline-flex items-center gap-1 text-xs font-medium tabular-nums"
//       style={{ color: STATUS_COLOR[status] }}
//     >
//       <StatusIcon status={status} className="size-4" />
//       {count}
//     </span>
//   );
// }

export default VerdictSection;
